import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  Grid,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Sidebar from '../../Pages/landingPage/Sidebar';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Business from '@mui/icons-material/Business';
import EditIcon from '../../Images/Edit.png';
import {
  fetchUsers,
  getOrganizationById,
  getOrganizations,
} from '../../apis/Admin';
import {
  Person4,
  SettingsRounded,
  TrackChanges,
  Announcement,
  PersonAdd,
  PersonRemove,
  CalendarMonth,
  Groups,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import OnboardingTour from '../../components/OnboardingTour';
import Cookies from 'js-cookie';
import UserDialog from '../UserDialog';
import AssignOrganizationDialog from '../AssignOrganizationDialog';
import { AdminTabs } from '../AdminTabs';
import { useTimezone } from '../../hooks/useTimezone';
import { DataTable } from '../../components/ui/table';
import { PageHeader } from '../../components/ui/header';
import { PrimaryButton } from '../../components/ui/button';
import { FilterSelect } from '../../components/ui/select';
import { SummaryCard } from '../../components/ui/card';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};
const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, type: 'spring', stiffness: 100 },
  },
};

export default function AdminUsers() {
  // eslint-disable-next-line
  const navigate = useNavigate();
  const { formatDate } = useTimezone();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    mode: 'create',
    user: null,
  });

  const [assignOrgDialog, setAssignOrgDialog] = useState({
    open: false,
    user: null,
  });

  const [pageError, setPageError] = useState(null);
  const [orgSeatInfo, setOrgSeatInfo] = useState(null);
  const [loadingSeatInfo, setLoadingSeatInfo] = useState(false);

  // Super admin check
  const rawRole = useMemo(
    () => Cookies.get('Role_id') ?? Cookies.get('role_id'),
    [],
  );

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  const isAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && (n === 2 || n === 1);
  }, [rawRole]);

  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [organizations, setOrganizations] = useState([]);

  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const usersData = await fetchUsers(token);
      const mappedUsers = usersData.map((u) => ({
        ...u,
        createdAt: u.created_at ? new Date(u.created_at).getTime() : 0,
      }));
      setUsers(mappedUsers);
      setPageError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setPageError('Error fetching user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  // Fetch organizations for Super Admin filter
  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchOrgs = async () => {
      try {
        const orgs = await getOrganizations();
        setOrganizations(orgs);
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      }
    };

    fetchOrgs();
  }, [isSuperAdmin]);

  // Fetch organization seat info for Admin users
  const fetchOrgSeatInfo = async () => {
    if (!isAdmin || isSuperAdmin) return; // Only for regular Admin, not Super Admin

    const orgId = Cookies.get('Org_id');
    if (!orgId) return;

    setLoadingSeatInfo(true);
    try {
      const orgData = await getOrganizationById(orgId);
      setOrgSeatInfo({
        total_seats: orgData.total_seats || 0,
        used_seats: orgData.used_seats || 0,
        available_seats:
          orgData.available_seats !== undefined
            ? orgData.available_seats
            : orgData.total_seats === 0
              ? -1
              : (orgData.total_seats || 0) - (orgData.used_seats || 0),
        org_name: orgData.org_name || '',
      });
    } catch (error) {
      console.error('Failed to fetch organization seat info:', error);
      // Don't show error to user, just silently fail
    } finally {
      setLoadingSeatInfo(false);
    }
  };

  useEffect(() => {
    fetchOrgSeatInfo();
  }, [isAdmin, isSuperAdmin]);

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.column === column && sortConfig.direction === 'asc')
      direction = 'desc';
    else if (sortConfig.column === column && sortConfig.direction === 'desc') {
      direction = 'none';
      column = null;
    }
    setSortConfig({ column, direction });
  };

  const applySort = (rows) => {
    if (sortConfig.direction === 'none' || !sortConfig.column) return rows;

    const arr = [...rows];

    return arr.sort((a, b) => {
      let A;
      let B;

      switch (sortConfig.column) {
        case 'name': {
          // Use first_name + last_name instead of a.name (since you don't have name)
          const aName = `${a.first_name || ''} ${a.last_name || ''}`;
          const bName = `${b.first_name || ''} ${b.last_name || ''}`;
          A = aName.trim().toLowerCase();
          B = bName.trim().toLowerCase();
          break;
        }

        case 'role': {
          A = (a.role_name || '').toLowerCase();
          B = (b.role_name || '').toLowerCase();
          break;
        }

        case 'status': {
          // is_active is boolean, don't call toLowerCase on it
          A = a.is_active ? 'active' : 'inactive';
          B = b.is_active ? 'active' : 'inactive';
          break;
        }

        case 'createdAt': {
          A = typeof a.createdAt === 'number' ? a.createdAt : 0;
          B = typeof b.createdAt === 'number' ? b.createdAt : 0;
          break;
        }

        case 'trial_status': {
          const aTrial = a.trial_end_date ? (new Date(a.trial_end_date) > new Date() ? 1 : 2) : 3;
          const bTrial = b.trial_end_date ? (new Date(b.trial_end_date) > new Date() ? 1 : 2) : 3;
          A = aTrial;
          B = bTrial;
          break;
        }

        default:
          return 0;
      }

      if (sortConfig.column === 'createdAt' || sortConfig.column === 'trial_status') {
        return sortConfig.direction === 'asc' ? A - B : B - A;
      }

      // Safety: ensure they're strings before localeCompare
      const strA = String(A);
      const strB = String(B);

      return sortConfig.direction === 'asc'
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });
  };

  const filtered = useMemo(() => {
    const safeUsers = Array.isArray(users) ? users : [];

    const q = search.trim().toLowerCase();
    const roleFilterNorm = (roleFilter || 'all').toLowerCase();
    const statusFilterNorm = (statusFilter || 'all').toLowerCase();

    const matchesQuery = (u) => {
      if (!q) return true;

      const firstName = (u.first_name || '').toLowerCase();
      const lastName = (u.last_name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const role = (u.role_name || '').toLowerCase();
      const orgName = (u.org_name || u.Org_name || '').toLowerCase();

      return (
        firstName.includes(q) ||
        lastName.includes(q) ||
        email.includes(q) ||
        role.includes(q) ||
        orgName.includes(q)
      );
    };

    const rows = safeUsers.filter((u) => {
      const roleName = (u.role_name || '').toLowerCase();
      const statusValue = u.is_active ? 'active' : 'inactive';

      const roleMatches =
        roleFilterNorm === 'all' || roleName === roleFilterNorm;

      const statusMatches =
        statusFilterNorm === 'all' || statusValue === statusFilterNorm;

      const orgMatches =
        !orgFilter || // when nothing selected
        orgFilter === 'all' || // "All" option
        (orgFilter === 'none' && !u.org_name) || // "No Organization" option
        u.org_name === orgFilter;

      return roleMatches && statusMatches && orgMatches && matchesQuery(u);
    });

    return applySort(rows);
  }, [users, search, roleFilter, statusFilter, orgFilter, sortConfig]);

  const displayed = showAll ? filtered : filtered.slice(0, 10);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.is_active === true).length;
    const inactive = users.filter((u) => u.is_active === false).length;
    
    // Calculate new users this month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const newThisMonth = users.filter((u) => {
      if (!u.created_at) return false;
      const createdDate = new Date(u.created_at);
      return createdDate >= startOfMonth;
    }).length;

    return { total, active, inactive, newThisMonth };
  }, [users]);

  const Pill = ({ children, color, bgColor }) => (
    <Box
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.35,
        borderRadius: '10px',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        color: bgColor ? '#FFFFFF' : color,
        bgcolor: bgColor || alpha(color, 0.12),
        border: `1px solid ${color}`,
        textTransform: 'capitalize',
      }}
    >
      {children}
    </Box>
  );

  Pill.propTypes = {
    children: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired,
    bgColor: PropTypes.string,
  };

  // Prepare table data
  const tableData = displayed.map((u, index) => ({
    ...u,
    srNo: index + 1,
  }));

  const tableColumns = [
    {
      key: 'name',
      label: 'User',
      sortable: true,
      render: (row) => (
        <Box>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#0F172A',
              fontFamily: 'Inter, sans-serif',
              mb: 0.25,
            }}
          >
            {`${row.first_name || ''} ${row.last_name || ''}`.trim() || '—'}
          </Typography>
          {row.email && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#6B7280',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {row.email}
            </Typography>
          )}
        </Box>
      ),
    },
    ...(isSuperAdmin
      ? [
          {
            key: 'organization',
            label: 'Organization',
            sortable: false,
            render: (row) => (
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#525252',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {row.org_name || 'No Organization'}
              </Typography>
            ),
          },
        ]
      : []),
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (row) => {
        const roleNameLower = (row.role_name || '').toLowerCase();
        const roleColor = roleNameLower === 'super admin' 
          ? '#3D9462' 
          : roleNameLower === 'admin'
          ? '#1B2640'
          : '#6b7280';
        const roleBgColor = roleNameLower === 'super admin' 
          ? '#3D9462' 
          : roleNameLower === 'admin'
          ? '#1B2640'
          : undefined;
        return <Pill color={roleColor} bgColor={roleBgColor}>{row.role_name || '—'}</Pill>;
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const statusColor = row.is_active === false ? '#ed6c02' : '#2e7d32';
        const statusText = row.is_active ? 'Active' : 'Inactive';
        return <Pill color={statusColor}>{statusText}</Pill>;
      },
    },
    ...(isSuperAdmin
      ? [
          {
            key: 'trial_status',
            label: 'Trial Status',
            sortable: true,
            render: (row) => {
              const hasOrg = row.org_id || row.organizationid || row.org_name;
              if (hasOrg) {
                return (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      fontStyle: 'italic',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    See Organization
                  </Typography>
                );
              }
              if (!row.trial_end_date) {
                return <Pill color="#d32f2f">Expired</Pill>;
              }
              const endDate = new Date(row.trial_end_date);
              const now = new Date();
              const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
              if (daysRemaining <= 0) {
                return <Pill color="#d32f2f">Expired</Pill>;
              }
              const color = daysRemaining <= 4 ? '#ed6c02' : '#2e7d32';
              return (
                <Pill color={color}>
                  {`${daysRemaining} days left`}
                </Pill>
              );
            },
          },
        ]
      : []),
    {
      key: 'created_at',
      label: 'Created Date',
      sortable: true,
      render: (row) => (
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#525252',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {row.created_at
            ? formatDate(row.created_at, {
                includeTime: true,
              })
            : 'N/A'}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          bgcolor: '#FAFBFD',
          minHeight: '100vh',
        }}
      >
        <Sidebar />
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <PageHeader
            title="Admin"
            createButtonText="+ New User"
            createButtonClick={() =>
              setDialog({ open: true, mode: 'create', user: null })
            }
            showSearchIcon={true}
            searchIconClick={() => setSearchOpen(!searchOpen)}
            searchOpen={searchOpen}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search users…"
            showConnectorIcon={false}
          />

          <Box sx={{ flexGrow: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
            <Collapse in={!!pageError} unmountOnExit>
              <Alert
                severity="error"
                onClose={() => setPageError(null)}
                sx={{
                  mb: 1,
                  border: '1px solid #fecaca',
                  background: '#fee2e2',
                  color: '#7f1d1d',
                }}
              >
                {pageError}
              </Alert>
            </Collapse>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Navigation Tabs */}
              <Box data-tour="admin-tabs" sx={{ mb: 1.5 }}>
              <AdminTabs
                items={[
                  ...(isSuperAdmin
                    ? [
                        {
                          to: '/admin/organizations',
                          label: 'Organizations',
                          icon: Business,
                        },
                      ]
                    : []),
                  { to: '/admin/user', label: 'User Management', icon: Person4 },
                  {
                    to: '/admin/annoucement',
                    label: 'Annoucement',
                    icon: Announcement,
                  },
                  {
                    to: '/admin/setting',
                    label: 'Application Settings',
                    icon: SettingsRounded,
                  },
                  {
                    to: '/admin/audit-log',
                    label: 'Audit Logs',
                    icon: TrackChanges,
                  },
                ]}
              />
            </Box>

            {/* Seat Information Card for Admin Users */}
            {isAdmin && !isSuperAdmin && orgSeatInfo && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    p: 2.5,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background:
                      'linear-gradient(135deg, #F8FAFF 0%, #EEF2FF 100%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#374151',
                          mb: 1,
                        }}
                      >
                        Organization Seat Information
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6b7280',
                              mb: 0.5,
                            }}
                          >
                            Total Seats
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '1rem',
                              fontWeight: 600,
                              color: '#111827',
                            }}
                          >
                            {orgSeatInfo.total_seats === 0
                              ? 'Unlimited'
                              : orgSeatInfo.total_seats}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6b7280',
                              mb: 0.5,
                            }}
                          >
                            Used Seats
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '1rem',
                              fontWeight: 600,
                              color: '#111827',
                            }}
                          >
                            {orgSeatInfo.used_seats}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6b7280',
                              mb: 0.5,
                            }}
                          >
                            Available Seats
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '1rem',
                              fontWeight: 600,
                              color:
                                orgSeatInfo.available_seats === -1
                                  ? '#6b7280'
                                  : orgSeatInfo.available_seats > 0
                                    ? '#2e7d32'
                                    : orgSeatInfo.available_seats === 0
                                      ? '#ed6c02'
                                      : '#d32f2f',
                            }}
                          >
                            {orgSeatInfo.available_seats === -1
                              ? 'Unlimited'
                              : orgSeatInfo.available_seats}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {orgSeatInfo.available_seats !== -1 &&
                      orgSeatInfo.available_seats <= 2 &&
                      orgSeatInfo.available_seats >= 0 && (
                        <Alert
                          severity={
                            orgSeatInfo.available_seats === 0 ? 'warning' : 'info'
                          }
                          sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiAlert-message': {
                              fontSize: '0.875rem',
                            },
                          }}
                        >
                          {orgSeatInfo.available_seats === 0
                            ? 'No seats available. Please contact Super Admin to increase seat limit.'
                            : `Only ${orgSeatInfo.available_seats} seat${
                                orgSeatInfo.available_seats === 1 ? '' : 's'
                              } remaining. Contact Super Admin if you need more.`}
                        </Alert>
                      )}
                  </Box>
                </Box>
              </motion.div>
            )}

            {isAdmin && !isSuperAdmin && loadingSeatInfo && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 2,
                }}
              >
                <CircularProgress size={24} sx={{ color: '#1B2640' }} />
              </Box>
            )}

            {/* Section Title */}
            <Box sx={{ mb: 1.5, mt: 1.5 }}>
              <Typography
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  fontWeight: 500,
                  color: '#4A5565',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Manage all Users and their Roles
              </Typography>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Total Users"
                  value={summaryMetrics.total}
                  icon={Groups}
                  iconColor="#155DFC"
                  iconBgColor="#DBEAFE"
                  variants={itemVariants}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Active Users"
                  value={summaryMetrics.active}
                  icon={PersonAdd}
                  iconColor="#059669"
                  iconBgColor="#DCFCE7"
                  variants={itemVariants}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="Inactive Users"
                  value={summaryMetrics.inactive}
                  icon={PersonRemove}
                  iconColor="#F58E01"
                  iconBgColor="#FEF3C7"
                  variants={itemVariants}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  title="New this Month"
                  value={summaryMetrics.newThisMonth}
                  icon={CalendarMonth}
                  iconColor="#9F22FC"
                  iconBgColor="#F3E8FF"
                  variants={itemVariants}
                />
              </Grid>
            </Grid>

            {/* Filters Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                mb: 2,
              }}
            >
              <FilterSelect
                label="Role"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setShowAll(false);
                }}
                options={[
                  { value: 'admin', label: 'Admin' },
                  { value: 'user', label: 'User' },
                ]}
                allOption={true}
                allOptionLabel="All"
                allOptionValue="all"
                sx={{ minWidth: 160 }}
              />

              {isSuperAdmin && (
                <FilterSelect
                  label="Organization"
                  value={orgFilter}
                  onChange={(e) => {
                    setOrgFilter(e.target.value);
                    setShowAll(false);
                  }}
                  options={[
                    { value: 'none', label: 'No Organization' },
                    ...organizations
                      .filter((org) => {
                        const orgName = (org.org_name || org.name || '').trim();
                        return orgName;
                      })
                      .map((org) => ({
                        value: org.org_name || org.name,
                        label: org.org_name || org.name,
                      })),
                  ]}
                  allOption={true}
                  allOptionLabel="All"
                  allOptionValue="all"
                  sx={{ minWidth: 180 }}
                />
              )}

              <FilterSelect
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setShowAll(false);
                }}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                allOption={true}
                allOptionLabel="All"
                allOptionValue="all"
                sx={{ minWidth: 180 }}
              />
            </Box>

            {/* Users Table */}
            <Card
              sx={{
                borderRadius: '4px',
                border: '1px solid #E5E7EB',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <DataTable
                  columns={tableColumns}
                  data={tableData}
                  loading={loading}
                  showSerialNumber={true}
                  showActions={true}
                  actionsColumnLabel="Action"
                  renderActions={(row) => (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDialog({
                              open: true,
                              mode: 'edit',
                              user: row,
                            });
                          }}
                          sx={{
                            color: '#6b7280',
                            p: '6px',
                            borderRadius: '6px',
                            '&:hover': {
                              backgroundColor: '#f3f4f6',
                            },
                          }}
                        >
                          <img
                            src={EditIcon}
                            alt="Edit"
                            style={{ width: 20, height: 20 }}
                          />
                        </IconButton>
                      </Tooltip>
                      {isSuperAdmin && (
                        <Tooltip title="Assign Organization">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignOrgDialog({
                                open: true,
                                user: row,
                              });
                            }}
                            sx={{
                              color: '#1B2640',
                              p: '6px',
                              borderRadius: '6px',
                              '&:hover': {
                                backgroundColor: '#EEF2FF',
                              },
                            }}
                          >
                            <Business sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  emptyMessage="No users found."
                  emptySubMessage={
                    isAdmin
                      ? 'Click "New User" to create one.'
                      : ''
                  }
                  containerSx={{
                    overflowY: 'hidden',
                  }}
                />
              </CardContent>
            </Card>

            {/* View All Button */}
            {filtered.length > 10 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                <PrimaryButton
                  variant="outlined"
                  onClick={() => setShowAll(!showAll)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    px: 2.5,
                    py: 0.75,
                  }}
                >
                  {showAll ? 'View Less' : `View All (${filtered.length})`}
                </PrimaryButton>
              </Box>
            )}
          </motion.div>
        </Box>
      </Box>
    </Box>

      <UserDialog
        open={dialog.open}
        mode={dialog.mode}
        user={dialog.user}
        onClose={() => setDialog({ open: false, mode: 'create', user: null })}
        onSubmit={async (data) => {
          if (dialog.mode === 'create') {
            await fetchUsersData();
            await fetchOrgSeatInfo(); // Refresh seat info after creating user
          } else {
            const userId =
              data.user_id ||
              data.id ||
              dialog.user?.user_id ||
              dialog.user?.id;
            if (userId) {
              setUsers((prev) =>
                prev.map((u) =>
                  u.user_id === userId || u.id === userId
                    ? { ...u, ...data }
                    : u,
                ),
              );
            }

            await fetchUsersData();
            await fetchOrgSeatInfo(); // Refresh seat info after editing user
          }
        }}
      />

      <AssignOrganizationDialog
        open={assignOrgDialog.open}
        user={assignOrgDialog.user}
        onClose={() => setAssignOrgDialog({ open: false, user: null })}
        onSuccess={async () => {
          await fetchUsersData();
          await fetchOrgSeatInfo(); // Refresh seat info after user operations
        }}
      />
      <OnboardingTour />
    </>
  );
}
